import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { SubscriptionTier, UserSubscription, UserUsage } from '@/components/admin/types';
import { featureFlags } from '@/utils/featureFlags';

/**
 * Get all available subscription tiers
 */
export const getSubscriptionTiers = async (): Promise<SubscriptionTier[]> => {
  try {
    const { data, error } = await supabase
      .from('subscription_tiers')
      .select('*')
      .eq('is_active', true)
      .order('price');

    if (error) throw error;
    return data || [];
  } catch (error: any) {
    console.error('Error fetching subscription tiers:', error);
    toast.error('Failed to load subscription plans');
    return [];
  }
};

/**
 * Get the current user's subscription
 */
export const getUserSubscription = async (userId: string): Promise<UserSubscription | null> => {
  try {
    const { data, error } = await supabase
      .from('user_subscriptions')
      .select(`
        *,
        tier:tier_id(*)
      `)
      .eq('user_id', userId)
      .eq('status', 'active')
      .maybeSingle();

    if (error) throw error;
    
    if (data) {
      const validStatus = data.status as "active" | "canceled" | "past_due";
      const typedSubscription: UserSubscription = {
        ...data,
        status: validStatus,
        tier: data.tier as SubscriptionTier
      };
      
      return typedSubscription;
    }
    
    return null;
  } catch (error: any) {
    console.error('Error fetching user subscription:', error);
    toast.error('Failed to load your subscription information');
    return null;
  }
};

/**
 * Check if user has premium access to Product Copilot
 */
export const hasProductCopilotAccess = async (userId: string): Promise<boolean> => {
  try {
    if (!featureFlags.enableSubscriptionLimits) {
      return true;
    }
    
    const { data: isAdmin } = await supabase.rpc('is_admin');
    if (isAdmin) {
      return true;
    }
    
    if (!userId) {
      return false;
    }
    
    const subscription = await getUserSubscription(userId);
    
    if (subscription?.tier && subscription.tier.name.toLowerCase().includes('premium')) {
      return true;
    }
    
    return false;
  } catch (error: any) {
    console.error('Error checking Product Copilot access:', error);
    return false;
  }
};

/**
 * Get the current user's usage for the current billing period
 */
export const getUserUsage = async (userId: string): Promise<UserUsage | null> => {
  try {
    const subscription = await getUserSubscription(userId);
    
    let promptCopiesLimit = 5;
    
    if (subscription?.tier) {
      promptCopiesLimit = subscription.tier.prompt_copy_limit;
    }
    
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    const { data, error } = await supabase
      .from('prompt_copies')
      .select('id', { count: 'exact' })
      .eq('user_id', userId)
      .gte('created_at', monthStart.toISOString())
      .lte('created_at', monthEnd.toISOString());

    if (error) throw error;
    
    return {
      user_id: userId,
      prompt_copies_used: data ? data.length : 0,
      prompt_copies_limit: promptCopiesLimit,
      period_start: monthStart.toISOString(),
      period_end: monthEnd.toISOString()
    };
  } catch (error: any) {
    console.error('Error fetching user usage:', error);
    toast.error('Failed to load your usage information');
    return null;
  }
};

/**
 * Check if the user is an admin
 */
const isUserAdmin = async (userId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase.rpc('is_admin');
    
    if (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
    
    return !!data;
  } catch (error) {
    console.error('Error in isUserAdmin:', error);
    return false;
  }
};

/**
 * Check if the user has reached their prompt copy limit
 */
export const canUserCopyPrompt = async (userId: string): Promise<{
  canCopy: boolean;
  usedCount: number;
  limit: number;
  percentUsed: number;
}> => {
  try {
    if (!featureFlags.enableSubscriptionLimits) {
      return { canCopy: true, usedCount: 0, limit: 999, percentUsed: 0 };
    }
    
    if (!userId) {
      return { canCopy: true, usedCount: 0, limit: 0, percentUsed: 0 };
    }
    
    const admin = await isUserAdmin(userId);
    if (admin) {
      return { canCopy: true, usedCount: 0, limit: 999999, percentUsed: 0 };
    }
    
    const usage = await getUserUsage(userId);
    
    if (!usage) {
      return { canCopy: true, usedCount: 0, limit: 0, percentUsed: 0 };
    }
    
    const { prompt_copies_used, prompt_copies_limit } = usage;
    const canCopy = prompt_copies_used < prompt_copies_limit;
    const percentUsed = prompt_copies_limit > 0 
      ? Math.round((prompt_copies_used / prompt_copies_limit) * 100) 
      : 0;
    
    return {
      canCopy,
      usedCount: prompt_copies_used,
      limit: prompt_copies_limit,
      percentUsed
    };
  } catch (error: any) {
    console.error('Error checking prompt copy limit:', error);
    return { canCopy: true, usedCount: 0, limit: 0, percentUsed: 0 };
  }
};
